'use client';

import styles from './index.module.scss';
import { pricingPlans } from './data';
import MainButton from "@/app/_components/Shared/MainButton";
import DirhamSymbol from "@/app/_components/Shared/DirhamSymbol";
import {useRouter} from "nextjs-toploader/app";

const PricingIcon = ({ type, isPopular }) => {
  const iconClass = isPopular ? styles.iconWhite : styles.icon;

  if (type === 'starter') {
    return (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    );
  } else if (type === 'growth') {
    return (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
      </svg>
    );
  } else if (type === 'pro') {
    return (
      <svg className={iconClass} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
      </svg>
    );
  }
  return null;
};

const CheckIcon = ({ isPopular }) => {
  const checkClass = isPopular ? styles.checkIconYellow : styles.checkIcon;

  return (
    <svg className={checkClass} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
    </svg>
  );
};

const MoneyIcon = ({ isPopular }) => {
  const iconClass = isPopular ? styles.moneyIconYellow : styles.moneyIcon;

  return (
    <svg className={iconClass} fill="currentColor" viewBox="0 0 20 20">
      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
    </svg>
  );
};

const StarIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const TextWithDirhamSymbol = ({ text, isPopular, symbolSize = 12 }) => {
  // Replace "Dh NUMBER" with "NUMBER [symbol]"
  const regex = /Dh\s+(\d+)/g;

  if (!regex.test(text)) {
    return <>{text}</>;
  }

  const parts = [];
  let lastIndex = 0;
  const matches = text.matchAll(/Dh\s+(\d+)/g);

  for (const match of matches) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the number
    parts.push(match[1]);

    // Add the symbol
    parts.push(
      <DirhamSymbol
        key={`symbol-${match.index}`}
        size={symbolSize}
        color={isPopular ? 'rgba(255, 255, 255, 0.8)' : '#4A5565'}
      />
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return <>{parts}</>;
};

const DocumentIcon = ({ isPopular }) => {
  const iconClass = isPopular ? styles.documentIconWhite : styles.documentIcon;

  return (
    <svg className={iconClass} fill="currentColor" viewBox="0 0 20 20">
      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
    </svg>
  );
};

const ClockIcon = () => (
  <svg className={styles.clockIcon} fill="currentColor" viewBox="0 0 20 20">
    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
  </svg>
);

export default function PricingSection() {
  const router = useRouter();

  const handleGetStarted = () => {
    router.push('/pro-services-request');
  };

  return (
    <section className={styles.container} id="pricing">
      <div className="container-wrapper">
        <div className={styles.header}>
          <div className={styles.badge}>
            <StarIcon className={styles.badgeIcon} />
            2025 PRO Packages
          </div>
          <h2 className={styles.title}>Choose Your Plan</h2>
          <p className={styles.description}>Transparent pricing. No hidden fees. Freeze anytime.</p>
        </div>

        <div className={styles.grid}>
          {pricingPlans.map((plan, index) => (
            <div
              key={index}
              className={`${styles.card} ${plan.popular ? styles.cardPopular : ''}`}
            >
              {plan.popular && (
                <div className={styles.popularBadge}>
                  <StarIcon className={styles.popularBadgeIcon} />
                  Most Popular
                </div>
              )}

              <div className={styles.cardHeader}>
                <div className={`${styles.iconWrapper} ${plan.popular ? styles.iconWrapperPopular : ''}`}>
                  <PricingIcon type={plan.icon} isPopular={plan.popular} />
                </div>
                <div>
                  <h3 className={`${styles.cardTitle} ${plan.popular ? styles.textWhite : ''}`}>{plan.name}</h3>
                  <p className={`${styles.cardSubtitle} ${plan.popular ? styles.textLight : ''}`}>{plan.subtitle}</p>
                </div>
              </div>

              <div className={`${styles.pricing} ${plan.popular ? styles.textWhite : ''}`}>
                <div className={styles.originalPrice}>
                  <span className={`${styles.strikethrough} ${plan.popular ? styles.textLight : ''}`}>
                    {plan.originalPrice} <DirhamSymbol
                      size={18}
                      color={plan.popular ? 'rgba(255, 255, 255, 0.5)' : '#99A1AF'}
                    />
                  </span>
                  <span className={`${styles.discountBadge} ${plan.popular ? styles.discountBadgePopular : ''}`}>
                    {plan.discount}
                  </span>
                </div>
                <div className={styles.currentPrice}>
                  <span className={styles.priceValue}>
                    {plan.price}
                    <DirhamSymbol
                      size={24}
                      color={plan.popular ? '#ffffff' : '#101828'}
                    />
                  </span>
                  <span className={`${styles.priceCurrency} ${plan.popular ? styles.textLight : ''}`}>
                    /month
                  </span>
                </div>
              </div>

              <div className={`${styles.annualPrice} ${plan.popular ? styles.annualPricePopular : ''}`}>
                <MoneyIcon isPopular={plan.popular} />
                <span className={`${plan.popular ? styles.textWhite : ''}`}>
                  {plan.annualPrice} <DirhamSymbol
                    size={14}
                    color={plan.popular ? '#ffffff' : '#364153'}
                  /> annually
                </span>
              </div>

              <div className={`${styles.features} ${plan.popular ? styles.textWhite : ''}`}>
                {plan.features.map((feature, idx) => (
                  <div key={idx} className={typeof feature === 'object' && feature.subtext ? styles.featureWrapper : ''}>
                    <div className={styles.feature}>
                      {idx === 0 && typeof feature === 'object' ? (
                        <DocumentIcon isPopular={plan.popular} />
                      ) : idx === 1 && typeof feature === 'object' ? (
                        <DocumentIcon isPopular={plan.popular} />
                      ) : (
                        <CheckIcon isPopular={plan.popular} />
                      )}
                      <div className={styles.featureText}>
                        <div className={styles.mainText}>
                          {typeof feature === 'string' ? (
                            <TextWithDirhamSymbol text={feature} isPopular={plan.popular} symbolSize={12} />
                          ) : (
                            feature.text
                          )}
                        </div>
                        {typeof feature === 'object' && feature.subtext && (
                          <div className={`${styles.subtext} ${plan.popular ? styles.textLight : ''}`}>
                            <TextWithDirhamSymbol text={feature.subtext} isPopular={plan.popular} symbolSize={11} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <MainButton
                color={plan.buttonColor}
                onClick={handleGetStarted}
              >
                {plan.buttonText}
              </MainButton>
            </div>
          ))}
        </div>

        <div className={styles.promoBanner}>
          <div className={styles.promoContent}>
            <div className={styles.promoIconWrapper}>
              <ClockIcon />
            </div>
            <div className={styles.promoText}>
              <h3 className={styles.promoTitle}>Limited-Time 2025 Promotion</h3>
              <p className={styles.promoDescription}>
                Lock in your exclusive 2025 rate before prices increase. Enjoy up to 30% off all year long and let Ninjaz handle the rest.
              </p>
            </div>
          </div>
          <MainButton
            color="white"
            onClick={handleGetStarted}
          >
            Claim Your Discount
          </MainButton>
        </div>
      </div>
    </section>
  );
}
